import React, { useState, useRef } from 'react';
import {
	Upload,
	X,
	Star,
	Image as ImageIcon,
	MoreVertical,
	Trash2,
	Download,
} from 'lucide-react';
import axios from 'axios';
import { saveAs } from 'file-saver';

const OnboardingImageSlotManager = ({
	imageType,
	maxSlots,
	title,
	description,
	client,
	clientImages,
	required = false,
	isProfileMode = false,
}) => {
	const {
		images,
		loading,
		error,
		uploadImage,
		deleteImage,
		setPrimary,
		getImagesByType,
		uploading,
	} = clientImages;

	const [showActions, setShowActions] = useState(null);
	const [downloadingImage, setDownloadingImage] = useState(null);
	const fileInputRefs = useRef({});

	const typeImages = getImagesByType(imageType);


	const handleFileSelect = async (file, slot) => {
		if (!file) return;

		// Validate file size (30MB limit for Vercel Blob)
		const maxSize = 30 * 1024 * 1024; // 30MB
		if (file.size > maxSize) {
			alert(`File is too large. Maximum size is 30MB. Your file is ${(file.size / 1024 / 1024).toFixed(2)}MB`);
			return;
		}

		// Validate file type
		const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
		if (!validTypes.includes(file.type)) {
			alert('Please upload a valid image file (JPEG, PNG, or GIF)');
			return;
		}

		try {
			console.log('Uploading file:', file.name, 'to slot:', slot);
			await uploadImage(imageType, slot, file);
		} catch (err) {
			console.error('Upload failed:', err);
			alert('Failed to upload image. Please try again.');
		}
	};

	const handleDelete = async (imageId) => {
		console.log('🗑️ DELETE CLICKED - Starting delete process for imageId:', imageId);

		if (window.confirm('Are you sure you want to delete this image?')) {
			try {
				console.log('🗑️ DELETE - About to call deleteImage with ID:', imageId);
				const result = await deleteImage(imageId);
				console.log('🗑️ DELETE - deleteImage result:', result);
				console.log('🗑️ DELETE - Closing dropdown menu');
				setShowActions(null);
			} catch (err) {
				console.error('🗑️ DELETE FAILED:', err);
				alert('Failed to delete image. Please try again.');
			}
		}
	};

	const handleSetPrimary = async (imageId) => {
		try {
			await setPrimary(imageId);
			setShowActions(null);
		} catch (err) {
			console.error('Set primary failed:', err);
			alert('Failed to set primary image. Please try again.');
		}
	};

	const handleDownload = async (image) => {
		if (!image || !image.imageUrl) return;

		setDownloadingImage(image.id);
		try {
			const response = await axios.get(image.imageUrl, {
				responseType: 'blob',
			});

			// Generate structured filename
			const urlParts = image.imageUrl.split('/');
			const originalFilename = urlParts[urlParts.length - 1];
			const extension = originalFilename.split('.').pop() || 'jpg';

			let filename;
			if (originalFilename.includes(client.username) && originalFilename.includes(imageType)) {
				filename = originalFilename;
			} else {
				const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
				filename = `${client.username}_${imageType}_${image.imageSlot}_${timestamp}.${extension}`;
			}

			saveAs(response.data, filename);
		} catch (error) {
			console.error('Download error:', error);
			alert('Failed to download image. Please try again.');
		} finally {
			setDownloadingImage(null);
			setShowActions(null);
		}
	};

	const getSlotName = (slot) => {
		switch (imageType) {
			case 'logo':
				return `Logo ${slot}`;
			case 'broker_logo':
				return `Logo ${slot}`;
			case 'headshot':
				return `Image ${slot}`;
			case 'lifestyle':
				return `Image ${slot}`;
			default:
				return `Image ${slot}`;
		}
	};

	// Handle drag and drop
	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e, slot) => {
		e.preventDefault();
		e.stopPropagation();
		
		const files = e.dataTransfer.files;
		if (files && files.length > 0) {
			handleFileSelect(files[0], slot);
		}
	};

	// Render a single slot
	const renderSlot = (slot) => {
		const image = typeImages.find((img) => img.imageSlot === slot);
		const isEmpty = !image;
		const isPrimary = image?.isPrimary;
		const isUploading = uploading[`${imageType}-${slot}`];


		return (
			<div
				key={slot}
				className='bg-white rounded-lg border border-gray-200 shadow-sm'
				onMouseLeave={() => setShowActions(null)}>
				{/* Header */}
				<div className={`border-b border-gray-100 flex items-center justify-between ${isProfileMode ? 'p-2' : 'p-3'}`}>
					<div className='flex items-center gap-2'>
						<span className={`font-medium text-gray-700 ${isProfileMode ? 'text-xs' : 'text-sm'}`}>
							{getSlotName(slot)}
						</span>
						{isPrimary && (
							<Star className={`text-yellow-500 fill-current ${isProfileMode ? 'w-3 h-3' : 'w-4 h-4'}`} />
						)}
					</div>

					{!isEmpty && !isUploading && (
						<div className='relative'>
							<button
								onClick={() =>
									setShowActions(showActions === slot ? null : slot)
								}
								className='p-1 hover:bg-gray-100 rounded'>
								<MoreVertical className='w-4 h-4 text-gray-500' />
							</button>

							{showActions === slot && (
								<div className='absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]'>
									<button
										onClick={() => handleDownload(image)}
										disabled={downloadingImage === image.id}
										className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed'>
										<Download className='w-3 h-3' />
										{downloadingImage === image.id
											? 'Downloading...'
											: 'Download'}
									</button>
									<button
										onClick={() =>
											fileInputRefs.current[`replace-${slot}`]?.click()
										}
										className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50'>
										Replace
									</button>
									{!isPrimary && typeImages.length > 1 && (
										<button
											onClick={() => handleSetPrimary(image.id)}
											className='w-full px-3 py-2 text-left text-sm hover:bg-gray-50'>
											Set Primary
										</button>
									)}
									<button
										onClick={() => handleDelete(image.id)}
										className='w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2'>
										<Trash2 className='w-3 h-3' />
										Delete
									</button>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Content */}
				<div className={isProfileMode ? 'p-2' : 'p-3'}>
					{isUploading ? (
						<div className={isProfileMode ? 'w-32 h-32' : 'aspect-square'}>
							<div className='w-full h-full border-2 border-dashed border-gray-300 rounded-lg flex flex-col justify-center items-center bg-gray-50'>
								<div className={`animate-spin rounded-full border-b-2 border-gray-600 mb-2 ${isProfileMode ? 'h-6 w-6' : 'h-8 w-8'}`}></div>
								<p className={`text-gray-600 ${isProfileMode ? 'text-xs' : 'text-sm'}`}>Uploading...</p>
							</div>
						</div>
					) : isEmpty ? (
						<div className={isProfileMode ? 'w-32 h-32' : 'aspect-square'}>
							<div
								className='w-full h-full border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-400 hover:bg-blue-50/50 transition-all cursor-pointer flex flex-col justify-center items-center'
								onClick={() => fileInputRefs.current[slot]?.click()}
								onDragOver={handleDragOver}
								onDrop={(e) => handleDrop(e, slot)}>
								<ImageIcon className={`text-gray-400 mb-2 ${isProfileMode ? 'w-6 h-6' : 'w-8 h-8'}`} />
								<p className={`text-gray-600 mb-1 ${isProfileMode ? 'text-xs' : 'text-sm'}`}>Click to upload</p>
								<p className={`text-gray-400 ${isProfileMode ? 'text-[10px]' : 'text-xs'}`}>or drag and drop</p>
								<p className={`text-gray-400 mt-2 ${isProfileMode ? 'text-[10px]' : 'text-xs'}`}>Max 30MB</p>
							</div>
						</div>
					) : (
						<div className='space-y-2'>
							{/* Image preview */}
							<div className={`${isProfileMode ? 'w-32 h-32' : 'aspect-square w-full'} rounded-lg relative overflow-hidden group flex items-center justify-center`}>
								<img
									src={image.imageUrl}
									alt={image.caption || `${imageType} ${slot}`}
									className='w-full h-full object-cover object-center'
								/>
								<div className='absolute inset-0 w-full h-full pointer-events-none bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all' />
							</div>

							{image.caption && !isProfileMode && (
								<div className='bg-gray-50 rounded p-2'>
									<p className='text-xs text-gray-500'>Caption:</p>
									<p className='text-sm text-gray-800'>{image.caption}</p>
								</div>
							)}
						</div>
					)}
				</div>

				{/* Hidden file inputs */}
				<input
					ref={(el) => (fileInputRefs.current[slot] = el)}
					type='file'
					accept='image/*'
					className='hidden'
					onChange={(e) => handleFileSelect(e.target.files[0], slot)}
				/>
				<input
					ref={(el) => (fileInputRefs.current[`replace-${slot}`] = el)}
					type='file'
					accept='image/*'
					className='hidden'
					onChange={(e) => handleFileSelect(e.target.files[0], slot)}
				/>
			</div>
		);
	};

	if (loading) {
		return (
			<div className='text-center py-8'>
				<div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
				<p className='mt-2 text-sm text-gray-500'>Loading images...</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className='text-center py-8'>
				<p className='text-red-600'>Error loading images: {error}</p>
			</div>
		);
	}

	// Generate slots array based on maxSlots
	const slots = Array.from({ length: maxSlots }, (_, i) => i + 1);

	return (
		<div className='space-y-4'>
			{description && (
				<p className='text-sm text-gray-600'>{description}</p>
			)}
			<div className={`grid gap-4 ${
				isProfileMode 
					? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
					: 'grid-cols-1 md:grid-cols-2'
			}`}>
				{slots.map((slot) => renderSlot(slot))}
			</div>
			{required && typeImages.length === 0 && (
				<p className='text-sm text-red-500 mt-2'>
					* At least one {title.toLowerCase()} is required
				</p>
			)}
		</div>
	);
};

export default OnboardingImageSlotManager;